import { useState, type DetailedHTMLProps, type ImgHTMLAttributes, type ReactNode } from 'react'
import { useChatImage } from '../../../../hooks/useChatImage'
import { twMerge } from 'tailwind-merge'
import { FileDown, Copy, Eye, CircleCheck } from 'lucide-react'

const cdnImageUrlRegex =
  /https?:\/\/[^\/]+\/api\/v1\/files\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i

function ImageButtons({
  copyStatus,
  onCopy,
  onSecondary,
  secondaryIcon,
  secondaryLabel,
}: {
  copyStatus: 'idle' | 'copied'
  onCopy: () => void
  onSecondary: () => void
  secondaryIcon: ReactNode
  secondaryLabel: string
}) {
  return (
    <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
      <span className="flex gap-2">
        <button
          onClick={onCopy}
          className="hover:bg-on-dark-hover bg-on-dark text-on-dark-inverse flex shrink-0 cursor-pointer items-center whitespace-nowrap rounded-xl px-3 py-2.5 backdrop-blur-md transition-all duration-200"
        >
          {copyStatus === 'copied' ? (
            <>
              <CircleCheck size={16} className="mr-1" />
              <span className="text-md font-medium">Copied!</span>
            </>
          ) : (
            <>
              <Copy size={16} className="mr-1" />
              <span className="text-md font-medium">Copy</span>
            </>
          )}
        </button>
        <button
          onClick={onSecondary}
          className="hover:bg-on-dark-hover bg-on-dark text-on-dark-inverse flex shrink-0 cursor-pointer items-center whitespace-nowrap rounded-xl px-3 py-2.5 backdrop-blur-md transition-all duration-200"
        >
          {secondaryIcon}
          <span className="text-md font-medium">{secondaryLabel}</span>
        </button>
      </span>
    </span>
  )
}

function ImagePlaceholder({ state, onView }: { state: 'loading' | 'error'; onView?: () => void }) {
  return (
    <div
      className={twMerge(
        'bg-light-5 flex h-[200px] w-[300px] items-center justify-center rounded-lg',
        state === 'error' && 'border-light-10 border',
      )}
    >
      <div className="text-light-40 text-center text-sm">
        {state === 'loading' ? (
          'Loading image...'
        ) : (
          <>
            <div>Failed to load image</div>
            {onView && (
              <button onClick={onView} className="text-light-60 hover:text-light-80 bg-light-5 mt-1 rounded-md text-xs">
                View
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

async function copyImageToClipboard(imageUrl: string): Promise<boolean> {
  try {
    const response = await fetch(imageUrl)
    const blob = await response.blob()
    await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })])
    return true
  } catch {
    try {
      await navigator.clipboard.writeText(imageUrl)
      return true
    } catch {
      return false
    }
  }
}

export const ImageComponent = ({
  src,
  alt,
}: DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>) => {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle')
  const [imageError, setImageError] = useState(false)
  const [imageLoading, setImageLoading] = useState(true)

  const uuid = src?.match(cdnImageUrlRegex)?.[1]

  // Hook must be called unconditionally (Rules of Hooks)
  const { imageUrl, imageError: cdnError } = useChatImage(uuid ?? '')

  if (!src) return <></>

  const handleImageLoad = () => {
    setImageLoading(false)
    setImageError(false)
  }

  const handleImageError = () => {
    setImageLoading(false)
    setImageError(true)
  }

  const handleCopy = async (url: string) => {
    const ok = await copyImageToClipboard(url)
    if (ok) {
      setCopyStatus('copied')
      setTimeout(() => setCopyStatus('idle'), 2000)
    }
  }

  // CDN image path
  if (uuid) {
    if (cdnError) {
      return (
        <span className="bg-danger-subtle text-danger-subtle inline-flex items-center gap-2 rounded-lg p-3">
          <span className="text-sm">Something went wrong with our image retrieval. Please try again.</span>
        </span>
      )
    }

    if (!imageUrl) return <></>

    return (
      <span className="group relative inline-block cursor-default">
        {imageLoading && <ImagePlaceholder state="loading" />}
        {imageError && (
          <ImagePlaceholder state="error" onView={() => window.open(imageUrl, '_blank', 'noopener,noreferrer')} />
        )}
        <img
          className={twMerge(
            'h-auto max-h-[200px] w-auto max-w-[300px] rounded-lg transition-all duration-200 group-hover:brightness-75',
            imageLoading && 'hidden',
            imageError && 'hidden',
          )}
          src={imageUrl}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
        {!imageLoading && !imageError && (
          <ImageButtons
            copyStatus={copyStatus}
            onCopy={() => handleCopy(imageUrl)}
            onSecondary={async () => {
              try {
                const response = await fetch(imageUrl)
                const blob = await response.blob()
                const downloadUrl = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = downloadUrl
                link.download = alt || 'image'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(downloadUrl)
              } catch (error) {
                console.error('Failed to download image:', error)
              }
            }}
            secondaryIcon={<FileDown size={16} className="mr-1" />}
            secondaryLabel="Save"
          />
        )}
      </span>
    )
  }

  // Regular image path
  return (
    <span className="group relative inline-block cursor-default">
      {imageLoading && <ImagePlaceholder state="loading" />}
      {imageError && (
        <ImagePlaceholder state="error" onView={() => window.open(src, '_blank', 'noopener,noreferrer')} />
      )}
      <img
        className={twMerge(
          'h-auto max-h-[200px] w-auto max-w-[300px] rounded-lg transition-all duration-200 group-hover:brightness-75',
          imageLoading && 'hidden',
          imageError && 'hidden',
        )}
        src={src}
        alt={alt}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
      {!imageLoading && !imageError && (
        <ImageButtons
          copyStatus={copyStatus}
          onCopy={() => handleCopy(src)}
          onSecondary={() => {
            const link = document.createElement('a')
            link.href = src
            link.target = '_blank'
            link.rel = 'noopener noreferrer'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }}
          secondaryIcon={<Eye size={16} className="mr-1" />}
          secondaryLabel="Open"
        />
      )}
    </span>
  )
}
